import { Circle, Polygon, Svg, Text as PdfText } from '@react-pdf/renderer';
import React from 'react';
import styled from 'styled-components';

type NorthArrowProps = {
  preview: boolean;
  invertBorderColor: boolean;
};

/**
 * NorthArrow component that displays a north arrow with "N" label
 * Used in CAD drawing reports to indicate north direction
 */
export const NorthArrow: React.FC<NorthArrowProps> = ({ preview, invertBorderColor }) => {
  const svgSize = { width: 40, height: 100 };
  const textPosition = { x: 20, y: 15 };
  const strokeColor = invertBorderColor ? 'white' : 'black';
  // 20,25 is the middle tip of the arrow. Both wind ccw to form the north arrow.
  const leftTrianglePoints = '20,25 20,50 5,65';
  const rightTrianglePoints = '20,25 35,65 20,50';

  if (preview) {
    return (
      <NorthArrowPreviewContainer>
        <svg
          height={svgSize.height}
          style={{ display: 'block' }}
          viewBox={`0 0 ${svgSize.width} ${svgSize.height}`}
          width={svgSize.width}
        >
          <polygon
            fill="white"
            points={leftTrianglePoints}
            stroke={strokeColor}
            strokeWidth="0.5"
          />

          <polygon
            fill="black"
            points={rightTrianglePoints}
            stroke={strokeColor}
            strokeWidth="0.5"
          />

          {/* White circle behind "N" for better visibility */}
          <circle
            cx={textPosition.x}
            cy={textPosition.y - 4}
            fill="white"
            r="8"
            stroke="black"
            strokeWidth="0.5"
          />

          {/* "N" label above the arrow */}
          <text
            fill="black"
            fontFamily="Helvetica-Bold"
            fontSize="12"
            textAnchor="middle"
            x={textPosition.x}
            y={textPosition.y}
          >
            N
          </text>
        </svg>
      </NorthArrowPreviewContainer>
    );
  }

  return (
    <Svg
      style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        width: `${svgSize.width}px`,
        height: `${svgSize.height}px`,
      }}
      viewBox={`0 0 ${svgSize.width} ${svgSize.height}`}
    >
      {/* Black triangle (left side of arrow) */}
      <Polygon fill="white" points={leftTrianglePoints} stroke={strokeColor} strokeWidth="0.5" />

      {/* White triangle (right side of arrow) */}
      <Polygon fill="black" points={rightTrianglePoints} stroke={strokeColor} strokeWidth="0.5" />

      {/* White circle behind "N" for better visibility */}
      <Circle
        cx={textPosition.x}
        cy={textPosition.y - 4}
        fill="white"
        r="8"
        stroke="black"
        strokeWidth="0.5"
      />

      {/* "N" label above the arrow */}
      <PdfText
        style={{
          fontSize: '12px',
          textAnchor: 'middle',
          fill: 'black',
          fontFamily: 'Helvetica-Bold',
        }}
        x={textPosition.x}
        y={textPosition.y}
      >
        N
      </PdfText>
    </Svg>
  );
};

const NorthArrowPreviewContainer = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
`;
